import React, { useMemo, useState } from "react";
import { Loader2, TrendingUp, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { WISHAUR_RED } from "components/LiveContestSidebar";
import { maskPhone } from "utils/prizeDisplay";

const PERIODS = [
  { id: "contest", label: "This contest" },
  { id: "day", label: "Today" },
  { id: "week", label: "Last 7 days" },
  { id: "month", label: "Last 30 days" },
];

const PeriodTabs = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1 p-0.5 rounded-lg bg-gray-100">
    {PERIODS.map((p) => (
      <button
        key={p.id}
        type="button"
        onClick={() => onChange(p.id)}
        className={`flex-1 min-w-[4.5rem] px-1.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${
          value === p.id
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

const displayUserLabel = (row) => {
  const name = row.name?.trim();
  if (!name) return "User";
  const digitsOnly = name.replace(/\D/g, "");
  if (digitsOnly.length >= 10 && digitsOnly.length === name.replace(/\s/g, "").length) {
    return maskPhone(name);
  }
  return name;
};

const RankList = ({ rows, valueKey, valueLabel, loading, emptyLabel }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <p className="text-xs text-gray-500 text-center py-6">{emptyLabel}</p>
    );
  }

  return (
    <div className="space-y-1">
      {rows.map((row, index) => (
        <div
          key={row.userId || index}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50"
        >
          <span
            className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
            style={{ backgroundColor: index < 3 ? WISHAUR_RED : "#94A3B8" }}
          >
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {displayUserLabel(row)}
            </p>
            {row.phone && (
              <p className="text-[10px] text-gray-500 font-mono truncate">
                {maskPhone(row.phone)}
              </p>
            )}
          </div>
          <span className="text-sm font-bold text-gray-900 tabular-nums shrink-0">
            {valueLabel(row[valueKey])}
          </span>
        </div>
      ))}
    </div>
  );
};

const PeriodRangeInfo = ({ period, periodRanges }) => {
  if (period !== "week" && period !== "month") return null;
  const label = periodRanges?.[period]?.label;
  if (!label) return null;
  return (
    <p className="mt-1.5 text-[9px] text-gray-500 text-center leading-tight">
      {label} <span className="text-gray-400">· IST</span>
    </p>
  );
};

const LiveDashboardStats = ({ stats, loading, onRefresh, refreshing }) => {
  const [entriesPeriod, setEntriesPeriod] = useState("contest");
  const [winningsPeriod, setWinningsPeriod] = useState("contest");

  const entryRows = useMemo(
    () => stats?.topEntries?.[entriesPeriod] ?? [],
    [stats, entriesPeriod]
  );
  const winningRows = useMemo(
    () => stats?.topWinnings?.[winningsPeriod] ?? [],
    [stats, winningsPeriod]
  );
  const periodRanges = stats?.periodRanges;

  return (
    <div className="flex flex-col gap-2 min-h-0 h-full lg:overflow-hidden">
      <Card className="border-0 shadow-sm rounded-xl flex flex-col overflow-hidden min-h-[16rem] lg:min-h-0 lg:flex-1">
        <CardHeader className="py-2 px-3 shrink-0 border-b border-gray-100">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-xs font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" style={{ color: WISHAUR_RED }} />
              Max entries · Top 10
            </CardTitle>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className="text-[10px] font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-50"
              >
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            )}
          </div>
          <div className="mt-2">
            <PeriodTabs value={entriesPeriod} onChange={setEntriesPeriod} />
            <PeriodRangeInfo period={entriesPeriod} periodRanges={periodRanges} />
          </div>
        </CardHeader>
        <CardContent className="p-2 flex-1 min-h-0 overflow-auto max-h-[20rem] lg:max-h-none">
          <RankList
            rows={entryRows}
            valueKey="entryCount"
            valueLabel={(v) => `${Number(v || 0).toLocaleString("en-IN")} entries`}
            loading={loading}
            emptyLabel={
              entriesPeriod === "contest"
                ? "No entries in this contest yet."
                : "No entries in this period yet."
            }
          />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm rounded-xl flex flex-col overflow-hidden min-h-[16rem] lg:min-h-0 lg:flex-1">
        <CardHeader className="py-2 px-3 shrink-0 border-b border-gray-100">
          <CardTitle className="text-xs font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" style={{ color: WISHAUR_RED }} />
            Top winnings · Top 10
          </CardTitle>
          <div className="mt-2">
            <PeriodTabs value={winningsPeriod} onChange={setWinningsPeriod} />
            <PeriodRangeInfo period={winningsPeriod} periodRanges={periodRanges} />
          </div>
        </CardHeader>
        <CardContent className="p-2 flex-1 min-h-0 overflow-auto max-h-[20rem] lg:max-h-none">
          <RankList
            rows={winningRows}
            valueKey="totalWinnings"
            valueLabel={(v) =>
              `₹ ${Number(v || 0).toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}`
            }
            loading={loading}
            emptyLabel={
              winningsPeriod === "contest"
                ? "No winnings in this contest yet."
                : "No winnings recorded in this period yet."
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveDashboardStats;
